import isEmpty from 'lodash/isEmpty';
import { lazy, Suspense } from 'react';
import { useParams } from 'react-router';
import type { RouteObject } from 'react-router-dom';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import SuspenseLoader from 'src/components/Loader';
import { IS_MINI_EXPERTS_DISABLED, SHOW_COMPONENT_SHOWCASE } from 'src/config';
import { NEW_CHAT_PATH } from 'src/content/ChatV2/constants';
import { APPS_NAV_LINK } from 'src/content/MiniExperts/constants';
import { SmartMeetProvider } from 'src/content/MiniExperts/SmartMeet/hooks/useSmartMeet';
import { ProjectsProvider } from 'src/content/Projects/hooks/use-projects';
import { TRIGGER_FORM_ROUTE } from 'src/content/Workflows/utils/triggerFormUrl';
import useCanUseWorkflows from 'src/hooks/useCanUseWorkflows';
import usePermission from 'src/hooks/usePermission';
import useSubscriptionPlan from 'src/hooks/useSubscriptionPlan';
import useUpdateAccessRights from 'src/hooks/useUpdateAccessRights';
import type { RootState } from 'src/redux/store';
import { useSelector } from 'src/redux/store';
import {
  CHAT_SOURCE_TYPES,
  FEATURE_PERMISSIONS,
  USER_APP_PERMISSIONS
} from 'src/types/enum';
import {
  ANALYTICS_ROUTES,
  NAVIGATIONS,
  ROUTES,
  USER_ROUTES
} from 'src/utils/constants';

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

const WorkFlows = Loader(lazy(() => import('src/content/Workflows')));
const WorkflowTriggerForm = Loader(
  lazy(() => import('src/content/Workflows/TriggerForm'))
);

// The hosted form lives on one id-based route - the link `Copy URL` shares -
// rather than a `/workflows/:slug` catch-all, so any other `/workflows/...`
// path still falls through to the 404 page.
const workflowTriggerFormRoutes: RouteObject[] = [
  {
    path: TRIGGER_FORM_ROUTE,
    element: <WorkflowTriggerForm />
  }
];

// Previously imported statically, which pulled their heavy deps (recharts,
// @devexpress grid, tiptap, react-pdf) into the initial bundle. Lazy-loaded so
// they ship in their own route chunks instead.
const MeetingDetails = Loader(
  lazy(
    () =>
      import(
        'src/content/MiniExperts/SmartMeet/components/MeetingDetails/index'
      )
  )
);
const Projects = Loader(lazy(() => import('src/content/Projects')));
const Skills = Loader(lazy(() => import('src/content/Skills')));
const ProjectDetails = Loader(
  lazy(() => import('src/content/Projects/components/project-details'))
);
const AdvancedSearch = Loader(lazy(() => import('src/content/AdvancedSearch')));
const UsageAndAnalytics = Loader(
  lazy(() => import('src/content/UsageAndAnalytics'))
);
const UsagePage = Loader(
  lazy(() => import('src/content/UsageAndAnalytics/UsagePage'))
);
const UserAndRoles = Loader(lazy(() => import('src/content/UserAndRoles')));

const DataLibrary = Loader(lazy(() => import('src/content/DataLibrary')));
const UploadSession = Loader(
  lazy(() => import('src/content/DataLibrary/UploadSession'))
);
const Chat = Loader(
  lazy(() => import('src/content/ChatV2/adapters/ConversationView'))
);
const SharedChat = Loader(
  lazy(() => import('src/content/ChatV2/adapters/SharedChatView'))
);
const ProjectChat = Loader(
  lazy(() => import('src/content/ChatV2/adapters/ProjectChatView'))
);
const CompareChat = Loader(
  lazy(() => import('src/content/ChatV2/adapters/CompareChatView'))
);
const DataAnalystChat = Loader(
  lazy(() => import('src/content/ChatV2/adapters/DataAnalystChatView'))
);
const AssistantChat = Loader(
  lazy(() => import('src/content/ChatV2/adapters/AssistantChatView'))
);
const Settings = Loader(lazy(() => import('src/content/Settings')));
const MiniExperts = Loader(lazy(() => import('src/content/MiniExperts')));
const AssistantNew = Loader(lazy(() => import('src/content/AssistantNew')));
const CreateUpdateAssistant = Loader(
  lazy(() => import('src/content/AssistantNew/CreateUpdateAssistant'))
);
const SharedAssistant = Loader(
  lazy(() => import('src/content/Assistant/ShareAssistant/index'))
);
const DocAnalyst = Loader(
  lazy(() => import('src/content/MiniExperts/DocAnalyst'))
);

const SmartMeet = Loader(
  lazy(() => import('src/content/MiniExperts/SmartMeet'))
);

const DataAnalyst = Loader(
  lazy(() => import('src/content/MiniExperts/DataAnalyst'))
);

const SharedSmartMeet = Loader(
  lazy(
    () => import('src/content/MiniExperts/SmartMeet/components/SharedSmartMeet')
  )
);

const AdminConsole = Loader(lazy(() => import('src/content/AdminConsole')));

const SmartMeetLayout = () => {
  return (
    <SmartMeetProvider>
      <Outlet />
    </SmartMeetProvider>
  );
};

const MeetingDetailsWrapper = () => {
  const location = useLocation();
  return <MeetingDetails key={location.key} />;
};

const Connectors = Loader(lazy(() => import('src/content/Connectors')));

const ProjectsLayout = () => {
  return (
    <ProjectsProvider>
      <Outlet />
    </ProjectsProvider>
  );
};

const ProjectDetailsWrapper = () => {
  const location = useLocation();
  return <ProjectDetails key={location.key} />;
};

const SharedProject = Loader(
  lazy(() => import('src/content/Projects/components/shared-project'))
);

const ComponentShowcase = Loader(
  lazy(() => import('src/content/ComponentShowcase'))
);
const ModelConfigurations = Loader(
  lazy(
    () =>
      import('src/content/AdminConsole/modelConfigurations/modelConfigurations')
  )
);

const ModelConfigurationGrid = Loader(
  lazy(() => import('src/content/AdminConsole/modelConfigurations/ModelGrid'))
);

const ModelCreateView = Loader(
  lazy(() => import('src/content/AdminConsole/modelConfigurations/ModelForm'))
);

const AdminNotificationSettings = Loader(
  lazy(() =>
    import('src/content/AdminConsole/notificationSettings').then((m) => ({
      default: m.AdminNotificationSettings
    }))
  )
);
const AdminOrganizationNotificationSettings = Loader(
  lazy(() =>
    import('src/content/AdminConsole/notificationSettings').then((m) => ({
      default: m.AdminOrganizationNotificationSettings
    }))
  )
);
const TenantGridView = Loader(
  lazy(() => import('src/content/AdminConsole/tenantGridView'))
);

const applicationsRoutes = (userAppPermissions) => {
  const { isSysAdminUser } = useUpdateAccessRights();
  const { hasBasicSubscription } = useSubscriptionPlan();
  const { chatContexts, selectedChat } = useSelector(
    (state: RootState) => state.chat
  );
  const chatType = chatContexts?.[selectedChat]?.chat_type;

  const hideCompareModelForEssentialUser =
    hasBasicSubscription && chatType === CHAT_SOURCE_TYPES.EmpowerGPT;

  const { user } = useSelector((state: RootState) => state.auth);

  const canUseChat = usePermission(FEATURE_PERMISSIONS.CHAT);
  const canUseWorkflows = useCanUseWorkflows();
  const canUseAssistants = usePermission(FEATURE_PERMISSIONS.ASSISTANTS);
  const canBuildAssistants = usePermission(
    FEATURE_PERMISSIONS.MANAGE_ASSISTANTS
  );
  const canUseAssistantChatFeature = usePermission(
    FEATURE_PERMISSIONS.ASSISTANT_CHAT
  );
  const canUseProjects = usePermission(FEATURE_PERMISSIONS.PROJECTS);
  const canUseProjectsChat = usePermission(FEATURE_PERMISSIONS.PROJECTS_CHAT);
  const canUseApps = usePermission(FEATURE_PERMISSIONS.APPS);
  const canUseAdvanceSearch = usePermission(FEATURE_PERMISSIONS.ADVANCE_SEARCH);
  const canUseConnectors = usePermission(FEATURE_PERMISSIONS.CONNECTORS);
  const canUseDocAnalyst = usePermission(FEATURE_PERMISSIONS.DOC_ANALYST);
  const canUseSmartMeet = usePermission(FEATURE_PERMISSIONS.SMART_MEET);
  const canUseDataAnalyst = usePermission(FEATURE_PERMISSIONS.DATA_ANALYST);
  const canUseWorkspaces = usePermission(
    FEATURE_PERMISSIONS.WORKSPACES_ENTERPRISE_KNOWLEDGE_CAPABILITY,
    { fallbackAppPermKey: USER_APP_PERMISSIONS.VIEW_ROOT_FOLDER_CONTENT }
  );

  // '/' lands on the first feature the user actually has access to - same
  // priority order as the sidebar nav.
  const featureRoutePriority: Array<{ available: boolean; path: string }> = [
    { available: canUseChat, path: NEW_CHAT_PATH },
    { available: canUseAssistants, path: NAVIGATIONS.ASSISTANTS },
    { available: canUseWorkflows, path: NAVIGATIONS.WORKFLOWS },
    { available: canUseProjects, path: NAVIGATIONS.PROJECTS },
    { available: canUseWorkspaces, path: NAVIGATIONS.DATA_LIBRARY },
    { available: canUseApps, path: NAVIGATIONS.APPS },
    { available: canUseConnectors, path: NAVIGATIONS.CONNECTORS },
    { available: canUseAdvanceSearch, path: NAVIGATIONS.ADVANCE_SEARCH }
  ];
  const defaultRoutePath =
    featureRoutePriority.find((route) => route.available)?.path ??
    NAVIGATIONS.ADVANCE_SEARCH;

  if (isSysAdminUser) {
    return [
      {
        path: '/chat',
        element: <Navigate to="/admin-console" replace />
      },
      {
        path: '/home',
        element: <Navigate to="/admin-console" replace />
      },
      {
        path: '/admin-console',
        element: <AdminConsole />,
        children: [
          {
            path: 'tenants',
            element: <TenantGridView />
          },
          {
            path: 'models',
            element: <ModelConfigurations />,
            children: [
              { path: 'configuration', element: <ModelConfigurationGrid /> },
              {
                path: 'upsert-model',
                element: <ModelCreateView />
              },
              { index: true, element: <Navigate to="configuration" replace /> }
            ]
          },
          {
            path: 'notifications',
            element: <AdminNotificationSettings />
          },
          {
            path: 'organization-notifications',
            element: <AdminOrganizationNotificationSettings />
          },
          {
            index: true,
            element: <Navigate to="tenants" replace />
          }
        ]
      },
      {
        path: ANALYTICS_ROUTES.INTERNAL_ANALYTICS,
        element: <UsageAndAnalytics />
      },
      {
        path: ANALYTICS_ROUTES.USAGE,
        element: <UsagePage />
      }
    ];
  } else {
    const routes: RouteObject[] = [
      {
        path: '/',
        element: <Navigate to={defaultRoutePath} replace />
      },
      {
        // Dedicated authenticated-only landing target for redirects that
        // happen outside the SPA router (post-login, post-signup, etc.) -
        // '/' can't be used for that because MTP deployments reserve it as
        // a public route (see PUBLIC_ROUTES / router/index.tsx), which would
        // send an authenticated user through PublicRoute's hard redirect
        // loop. See DEFAULT_APPLICATION_ROUTE in utils/constants.ts.
        path: '/home',
        element: <Navigate to={defaultRoutePath} replace />
      },
      {
        path: '/share-chat/:shareChatId',
        element: <SharedChat />
      },
      {
        path: '/share-assistant/:uuid',
        element: <SharedAssistant />
      },
      {
        path: NAVIGATIONS.SKILLS,
        element: <Skills />
      },
      {
        path: '/settings/:selectedTab',
        element: <Settings />
      }
    ];

    if (canUseChat) {
      routes.push(
        {
          path: '/chat',
          element: <Chat />
        },
        {
          path: '/chat/:chatId',
          element: <Chat />
        }
      );
    }

    if (canUseWorkflows) {
      routes.push(
        {
          path: '/workflows',
          element: <WorkFlows />
        },
        {
          path: '/workflows/builder',
          element: <WorkFlows />
        },
        ...workflowTriggerFormRoutes
      );
    }

    if (canUseAdvanceSearch) {
      routes.push({
        path: NAVIGATIONS.ADVANCE_SEARCH,
        element: <AdvancedSearch />
      });
    }

    if (canUseConnectors) {
      routes.push({
        path: '/connectors',
        element: <Connectors />
      });
    }

    if (canUseAssistants) {
      routes.push({
        path: NAVIGATIONS.ASSISTANTS,
        element: <AssistantNew />
      });
      routes.push({
        path: `${NAVIGATIONS.ASSISTANTS}/:id`,
        element: canBuildAssistants ? (
          <CreateUpdateAssistant />
        ) : (
          <Navigate to={NAVIGATIONS.ASSISTANTS} replace />
        )
      });
      if (canUseAssistantChatFeature) {
        routes.push(
          {
            path: `${NAVIGATIONS.ASSISTANTS}/:assistantId/chat/:chatId`,
            element: <AssistantChat />
          },
          {
            path: `${NAVIGATIONS.ASSISTANTS}/:assistantId/chat`,
            element: <AssistantChat />
          }
        );
      }
    }

    if (canUseProjects) {
      routes.push(
        {
          path: NAVIGATIONS.PROJECTS,
          element: <ProjectsLayout />,
          children: [
            { index: true, element: <Projects /> },
            ...(canUseProjectsChat
              ? [
                  { path: ':projectId/chat/:chatId', element: <ProjectChat /> },
                  { path: ':projectId/chat', element: <ProjectChat /> }
                ]
              : []),
            { path: ':projectId', element: <ProjectDetailsWrapper /> }
          ]
        },
        {
          path: '/share-project/:uuid',
          element: <SharedProject />
        }
      );
    }

    if (canUseWorkspaces) {
      routes.push(
        {
          path: '/data-library',
          element: <DataLibrary />
        },
        {
          path: '/data-library/:id',
          element: <DataLibrary />
        }
      );
      routes.push({
        path: `/data-library/${ROUTES.NAMESPACE_ROUTE}`,
        element: <Navigate to="/data-library" replace />
      });
      routes.push({
        path: `/data-library/${ROUTES.WORKSPACE_ROUTE}/:id`,
        element: <RedirectDataLibraryWithId />
      });
      routes.push({
        path: `/data-library/${ROUTES.RESOURCES_ROUTE}/:id`,
        element: <RedirectDataLibraryWithId />
      });
      routes.push(
        {
          path: '/data-library/upload-sessions',
          element: <UploadSession />
        },
        {
          path: '/data-library/upload-sessions/:sessionId',
          element: <UploadSession />
        }
      );
    }

    if (canUseChat && !hideCompareModelForEssentialUser) {
      routes.push({
        path: '/compare-models-chat',
        element: <CompareChat />
      });
    }
    // Add ComponentShowcase route only when enabled via ENV
    if (SHOW_COMPONENT_SHOWCASE) {
      routes.push({
        path: '/component-showcase',
        element: <ComponentShowcase />
      });
    }

    if (!IS_MINI_EXPERTS_DISABLED && canUseApps) {
      routes.push({
        path: '/apps',
        element: <MiniExperts />
      });

      if (canUseDocAnalyst) {
        routes.push({
          path: '/apps/doc-analyst/:selectedTab',
          element: <DocAnalyst />
        });
      }

      if (canUseSmartMeet) {
        routes.push(
          {
            path: APPS_NAV_LINK.SMART_MEET,
            element: <SmartMeetLayout />,
            children: [
              { index: true, element: <SmartMeet /> },
              { path: ':id', element: <MeetingDetailsWrapper /> }
            ]
          },
          {
            path: '/share-smart-meet/:uuid',
            element: <SharedSmartMeet />
          }
        );
      }

      if (canUseDataAnalyst) {
        routes.push({
          path: APPS_NAV_LINK.DATA_ANALYST,
          element: <Outlet />,
          children: [
            { index: true, element: <DataAnalyst /> },
            { path: 'chat', element: <DataAnalystChat /> },
            { path: 'chat/:chatId', element: <DataAnalystChat /> },
            { path: ':id', element: <MeetingDetails /> }
          ]
        });
      }
    }

    if (!isEmpty(userAppPermissions)) {
      if (userAppPermissions?.[USER_APP_PERMISSIONS.VIEW_USERS]?.value) {
        routes.push({
          path: `/${USER_ROUTES.USERS}`,
          element: <UserAndRoles />
        });
      }
      if (userAppPermissions?.[USER_APP_PERMISSIONS.VIEW_ROLES]?.value) {
        routes.push({
          path: `/${USER_ROUTES.ROLES}`,
          element: <UserAndRoles />
        });
      }
    }

    if (
      userAppPermissions?.[USER_APP_PERMISSIONS.VIEW_USER_GROUPS]?.value ||
      userAppPermissions?.[USER_APP_PERMISSIONS.VIEW_OWN_USER_GROUPS]?.value
    ) {
      routes.push({
        path: `/${USER_ROUTES.USER_GROUPS}`,
        element: <UserAndRoles />
      });
    }
    if (userAppPermissions?.[USER_APP_PERMISSIONS.VIEW_ANALYTICS]?.value) {
      routes.push(
        {
          path: ANALYTICS_ROUTES.INTERNAL_ANALYTICS,
          element: <UsageAndAnalytics />
        },
        {
          path: ANALYTICS_ROUTES.USAGE,
          element: <UsagePage />
        }
      );
    }

    return routes;
  }
};

const RedirectDataLibraryWithId = () => {
  const { id } = useParams();
  return <Navigate to={`/data-library/${id}`} replace />;
};

export default applicationsRoutes;
